/**
 * A Firestore-safe vocation document id. `cdl` is retained as an ergonomic
 * literal because it is the long-standing, canonical vocation; every
 * extensible id must pass through `parseVocationId` before it can be stored or
 * used as a Firestore path.
 */
declare const vocationIdBrand: unique symbol;

export type VocationId =
  | "cdl"
  | (string & { readonly [vocationIdBrand]: "VocationId" });

export const VOCATION_ID_PATTERN = /^[a-z][a-z0-9_]*$/;

export const CDL_VOCATION_ID: VocationId = "cdl";

/** Returns true only for ids that are safe Firestore vocation path segments. */
export function isVocationId(value: unknown): value is VocationId {
  return typeof value === "string" && VOCATION_ID_PATTERN.test(value);
}

/** Parses an external vocation id without allowing an unchecked type cast. */
export function parseVocationId(value: unknown): VocationId | undefined {
  return isVocationId(value) ? value : undefined;
}

/**
 * Any stored or external value → a usable `VocationId`, defaulting to CDL.
 *
 * This is the one spelling of "what vocation is this?" for a raw value. Reach
 * for it whenever you need the id itself (to load a doc, to report a trait);
 * reach for `isCdlVocation` when you only need the boolean.
 *
 * Everything `parseVocationId` rejects lands on CDL — `undefined` (legacy
 * accounts, which omit the field), `null`, `""`, and any pattern-violating
 * string. The user doc is cast rather than validated on read, so those shapes
 * are reachable at runtime even though the declared type excludes them. CDL is
 * the safe landing: the repo's own repair script buckets `undefined | null | ""`
 * as "needs the CDL default" (functions/scripts/backfillUserVocationIds.ts), and
 * treating garbage as a *non-CDL* vocation sends a CDL student down the wrong
 * funnel — or tries to load a vocation document that cannot exist.
 *
 * Note only ONE default is needed: `parseVocationId` takes `unknown` and already
 * rejects nullish input, so `parseVocationId(v ?? CDL) ?? CDL` does the work
 * twice.
 */
export function resolveVocationId(value: unknown): VocationId {
  return parseVocationId(value) ?? CDL_VOCATION_ID;
}

/**
 * Strict id equality. Returns FALSE for `undefined`, so callers reading
 * `user.vocationId` directly want `isCdlVocation` (for the boolean) or
 * `resolveVocationId` (for the value) — use this only on an id that is already
 * a `VocationId` by contract.
 */
export function isCdl(vocationId: VocationId | undefined): boolean {
  return vocationId === CDL_VOCATION_ID;
}

/**
 * True for CDL, including legacy accounts that predate `vocationId` — the field
 * is omitted at signup for CDL, so `undefined` means CDL, not "unknown". Null,
 * `""`, and unparseable ids resolve to CDL too; see `resolveVocationId`.
 */
export function isCdlVocation(vocationId: VocationId | undefined): boolean {
  return isCdl(resolveVocationId(vocationId));
}

/**
 * True for any vocation that is not CDL. Named for what it tests rather than
 * when the vocation was added — `diesel_tech` and `hvac` stop being "new", and
 * every vocation after them would read as "new" forever.
 *
 * Returns a plain boolean, not a type predicate: `isNonCdlVocation("cdl")` is
 * `false` for a perfectly valid `VocationId`, so a `vocationId is VocationId`
 * narrowing would lie about the false branch.
 */
export function isNonCdlVocation(vocationId: VocationId | undefined): boolean {
  return !isCdlVocation(vocationId);
}
